/**
 * Global Audio Coordinator
 *
 * Ensures only one audio source plays at a time across the entire app.
 * Uses an event-based pattern to notify all audio players when a new
 * playback is requested.
 */

type StopCallback = (requesterId: string) => void

class AudioCoordinator {
  private static instance: AudioCoordinator | null = null
  private listeners: Map<string, StopCallback> = new Map()
  private activeId: string | null = null

  private constructor() {
    // Private constructor for singleton pattern
  }

  /**
   * Get the singleton instance of AudioCoordinator
   */
  static getInstance(): AudioCoordinator {
    if (!AudioCoordinator.instance) {
      AudioCoordinator.instance = new AudioCoordinator()
    }
    return AudioCoordinator.instance
  }

  /**
   * Request exclusive playback for a specific audio instance.
   * This will stop all other audio instances before allowing this one to play.
   *
   * @param id - Unique identifier for the audio instance requesting playback
   */
  requestPlayback(id: string): void {
    // Notify all other listeners to stop
    this.listeners.forEach((callback, listenerId) => {
      if (listenerId !== id) {
        callback(id)
      }
    })

    this.activeId = id
  }

  /**
   * Release playback for a specific audio instance.
   * Called when audio is paused or the component unmounts.
   *
   * @param id - Unique identifier for the audio instance releasing playback
   */
  releasePlayback(id: string): void {
    if (this.activeId === id) {
      this.activeId = null
    }
  }

  /**
   * Subscribe to stop requests from other audio instances.
   *
   * @param id - Unique identifier for this audio instance
   * @param callback - Function to call when this instance should stop
   * @returns Cleanup function to unsubscribe
   */
  subscribe(id: string, callback: StopCallback): () => void {
    this.listeners.set(id, callback)

    return () => {
      this.listeners.delete(id)
      if (this.activeId === id) {
        this.activeId = null
      }
    }
  }

  /**
   * Check if a specific audio instance is the active one
   *
   * @param id - Unique identifier to check
   */
  isActive(id: string): boolean {
    return this.activeId === id
  }

  /**
   * Get the currently active audio instance ID
   */
  getActiveId(): string | null {
    return this.activeId
  }

  /**
   * Reset the coordinator state (for testing only)
   */
  reset(): void {
    this.listeners.clear()
    this.activeId = null
  }

  /**
   * Reset the singleton instance (for testing only)
   */
  static resetInstance(): void {
    if (AudioCoordinator.instance) {
      AudioCoordinator.instance.reset()
    }
  }
}

// Export singleton instance getter
export const getAudioCoordinator = AudioCoordinator.getInstance.bind(AudioCoordinator)

// Export class for testing (resetInstance) and type usage
export { AudioCoordinator }
